import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Search, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../lib/apiClient';
import { buildAccountMatcher, matchOne } from './matchAccounts';

// Inline "add an account" search field for the list detail view.
// Type to search across acronym / bu_code / official_name etc, click to add.
export default function AccountAddSearch({ existingDepartmentIds, onAdd, disabled }) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const departments = useQuery({
    queryKey: ['departments', 'all-for-matching'],
    queryFn: () => apiClient.get('/api/departments'),
    staleTime: 5 * 60 * 1000,
  });

  const fuse = useMemo(
    () => departments.data ? buildAccountMatcher(departments.data) : null,
    [departments.data]
  );

  const results = useMemo(() => {
    if (!fuse || !query.trim()) return [];
    const excluded = new Set(existingDepartmentIds);
    return matchOne(fuse, query, { limit: 8 })
      .filter(c => !excluded.has(c.department.department_id))
      .map(c => c.department);
  }, [fuse, query, existingDepartmentIds]);

  // Close menu when clicking outside
  useEffect(() => {
    const handle = (e) => {
      if (!inputRef.current) return;
      if (!inputRef.current.contains(e.target)) setFocused(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const handleAdd = (dept) => {
    onAdd({
      department_id: dept.department_id,
      salesforce_account_name: dept.official_name || dept.common_name,
      salesforce_territory: null,
      match_confidence: null,
      match_source: 'manual',
      _dept: dept,
    });
    setQuery('');
    setFocused(false);
  };

  return (
    <div className="al-add-search" ref={inputRef}>
      <div className="al-add-input-wrap">
        <Search size={16} className="al-add-icon" />
        <input
          type="text"
          className="al-add-input"
          value={query}
          placeholder={disabled ? 'Select a list first' : 'Add an account — search by name, acronym, or BU code'}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          disabled={disabled}
        />
      </div>
      {focused && results.length > 0 && (
        <ul className="al-add-dropdown" role="listbox">
          {results.map(d => (
            <li key={d.department_id}>
              <button type="button" onClick={() => handleAdd(d)}>
                <div className="al-add-result-primary">
                  {d.acronym && <span className="al-add-acronym">{d.acronym}</span>}
                  <span>{d.official_name || d.common_name}</span>
                </div>
                <div className="al-add-result-secondary">
                  {d.bu_code ? `BU ${d.bu_code}` : 'no BU code'}
                </div>
                <Plus size={14} className="al-add-plus" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
